import {
  ageDecadeFormatter,
  genderI18n,
  getReviewImagePath
} from '@lifedot/utils'
import { useShortCodeDataCemeteries } from '@/hooks/use-short-code-data/use-short-code-data'
import { Rating } from '@lifedot/atoms/Rating'
import Image from 'next/image'
import { FC } from 'react'
import { router } from '@lifedot/router'
import Link from 'next/link'
import { css } from '@emotion/react'
import { palette } from '@lifedot/styles/palette'
import { typography } from '@lifedot/styles/typography'
import { mq } from '@lifedot/styles/mediaQuery'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import { EventTracker } from '@lifedot/tracking'
import { elementCategories } from '@lifedot/constants/elementCategories'
import { SubHeading } from '@/components/ShortCode/CemeteryIntroduction/SubHeading'

const styles = {
  reviewSummary: css({
    display: 'flex',
    alignItems: 'center'
  }),
  score: css({
    marginLeft: 4
  }),
  count: css({
    color: palette.gray6
  }),
  review: css({
    marginTop: 12,
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${palette.gray2}`,
    gap: 16,
    [mq('sp')]: {
      padding: 16
    },
    p: {
      margin: 0
    }
  }),
  reviewer: css({
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: '36px 1fr',
    gap: 8,
    fontWeight: 'bold'
  }),
  linkWrap: css({
    textAlign: 'right',
    marginTop: 8
  }),
  link: css({
    display: 'inline-flex',
    alignItems: 'center',
    color: palette.main03
  })
}

interface ReviewProps {
  cemeteryId: number
}

export const Review: FC<ReviewProps> = ({ cemeteryId }) => {
  const [cemetery] = useShortCodeDataCemeteries([cemeteryId])
  if (!cemetery) return null

  const { review_summary: reviewSummary, name } = cemetery
  const { valuable_review: valuableReview } = reviewSummary
  if (!valuableReview) return null

  const path = getReviewImagePath({ ...valuableReview })
  const { age, gender, answers } = valuableReview
  const answer = answers.find(({ code }) => code === 'traffic')?.text

  return (
    <section>
      <SubHeading text={`${name}の口コミ`} />
      <div css={styles.reviewSummary}>
        総合評価
        <div css={styles.score}>
          <Rating point={reviewSummary.total_score ?? 0} />
        </div>
        <span css={[typography.textS, styles.count]}>
          （{reviewSummary.count}件）
        </span>
      </div>
      {answer && (
        <div css={styles.review}>
          <div css={styles.reviewer}>
            <Image
              src={path}
              alt={`${ageDecadeFormatter(age)}・${genderI18n(gender)}`}
              width={48}
              height={48}
            />
            <p css={typography.textS}>{`${ageDecadeFormatter(
              age
            )}・${genderI18n(gender)}`}</p>
          </div>
          <p css={typography.textM}>{answer}</p>
        </div>
      )}
      <div css={styles.linkWrap}>
        <EventTracker
          action="click"
          label={`cemeteryIntroduction_review_${cemeteryId}`}
          elementCategory={elementCategories.cemeteryIntroduction}
          elementLabel={`review_${cemeteryId}`}
          clone
        >
          <Link href={router.review(cemeteryId)} passHref prefetch={false}>
            <a css={[typography.textM, styles.link]}>
              口コミをすべて見る（{reviewSummary.count}件）
              <ChevronRightIcon fontSize="small" />
            </a>
          </Link>
        </EventTracker>
      </div>
    </section>
  )
}
